import {formatBytes, useStorageUsage} from '@/shared/utils'
import React, {useContext, useEffect, useRef} from 'react'

import ReactTooltip from 'react-tooltip'
import {FileTableContext} from './FileTable'
import {
  Crumb,
  CrumbTarget,
  locationCrumbs,
  MY_FILES_TOOLBAR_ACTIONS,
  ToolbarActions,
  toolbarActions,
} from './fileBrowserLocation'
import {OWN_ROOT} from '../core/storage'
import {ImportDataDropdown} from './ImportDataDropdown'
import {ImportsDropdown} from './imports/ImportsDropdown'
import {StorageUsageDropdown} from './storage/StorageUsageDropdown'
import {PendingShareInvitationsButton} from '@/app/modules/sharing/PendingShareInvitations'
import './file-table-header.scss'

interface HeaderProps {
  title: string
  hideTitle?: boolean
  collapseProps?: {
    'data-collapsed': boolean
    onClick: React.MouseEventHandler<HTMLButtonElement>
  }
  /**
   * 'panel' lifts the chrome actions into the Files lateral-panel header (icon buttons next to
   * close, matching the AI chat). 'picker' is browse-only. Styling of the remaining breadcrumbs
   * is otherwise identical.
   */
  variant?: 'default' | 'panel' | 'picker'
  /** Closes the Files lateral panel. Only used when {@link variant} is `'panel'`. */
  onClose?: () => void
}

type ChromeAppearance = 'labeled' | 'icon'

const RefreshIcon: React.FC<{spinning: boolean}> = ({spinning}) => (
  <span
    className={`ob-file-refresh-icon${spinning ? ' is-spinning' : ''}`}
    data-testid='omics-files-refresh-icon'
    data-spinning={spinning ? 'true' : 'false'}
    aria-hidden='true'
  >
    <i className='fas fa-sync-alt' />
  </span>
)

const labeledBtnStyle: React.CSSProperties = {
  padding: 'var(--ob-spacing-1) var(--ob-spacing-2)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--ob-spacing-1)',
  fontSize: 'var(--ob-font-size-sm)',
  fontWeight: '500',
  transition: 'all 0.2s ease',
}

const FileTableChromeActions = ({
  appearance,
  toolbar,
  picker,
}: {
  appearance: ChromeAppearance
  /** What can be done where the browser is (see toolbarActions). */
  toolbar: ToolbarActions
  /** The file-pick dialogs only browse: no actions at all, not even Refresh. */
  picker: boolean
}): JSX.Element => {
  const fileTableContext = useContext(FileTableContext)
  const usageQuery = useStorageUsage({enabled: toolbar.usage && appearance === 'labeled'})
  const usage = usageQuery.data ?? 0
  const newFolderOff = toolbar.newFolder.enabled ? undefined : toolbar.newFolder.reason
  const onNewFolder = () => {
    if (!newFolderOff) fileTableContext.onNewFolder()
  }
  const importDropdown = (
    <ImportDataDropdown
      appearance={appearance}
      onUploadFile={fileTableContext.onUploadFile}
      onUploadFolder={fileTableContext.onUploadFolder}
      onImportS3={fileTableContext.onImportS3}
      uploadDisabledReason={toolbar.upload.enabled ? undefined : toolbar.upload.reason}
      importDisabledReason={toolbar.import.enabled ? undefined : toolbar.import.reason}
    />
  )

  const refreshButton = (
    <button
      type='button'
      data-tip
      data-for='refresh-files'
      className={appearance === 'icon' ? 'ob-files-panel__icon-btn' : 'btn btn-sm'}
      style={appearance === 'icon' ? undefined : labeledBtnStyle}
      onMouseEnter={
        appearance === 'icon'
          ? undefined
          : (e) => {
              e.currentTarget.style.color = '#dbde8e'
              e.currentTarget.style.borderColor = '#dbde8e'
            }
      }
      onMouseLeave={
        appearance === 'icon'
          ? undefined
          : (e) => {
              e.currentTarget.style.color = ''
              e.currentTarget.style.borderColor = ''
            }
      }
      onClick={() => fileTableContext.onRefresh()}
      title='Refresh file list'
      aria-label='Refresh file list'
      aria-busy={fileTableContext.isRefetching}
      data-testid='omics-files-refresh-btn'
    >
      <RefreshIcon spinning={fileTableContext.isRefetching} />
      {appearance === 'labeled' && <span className='ob-file-header__btn-label'>Refresh</span>}
    </button>
  )

  if (appearance === 'icon') {
    return (
      <>
        {!picker && (
          <button
            type='button'
            className={`ob-files-panel__icon-btn${newFolderOff ? ' ob-toolbar-disabled' : ''}`}
            onClick={onNewFolder}
            title={newFolderOff ?? 'New folder'}
            aria-label='New folder'
            aria-disabled={newFolderOff ? true : undefined}
            data-testid='omics-files-new-folder-btn'
          >
            <i className='fas fa-folder-plus' />
          </button>
        )}
        {!picker && importDropdown}
        {!picker && (
          <ImportsDropdown appearance='icon' onOpenFolder={fileTableContext.onOpenFolder} />
        )}
        {toolbar.usage && <StorageUsageDropdown />}
        {/* Shared-folder invitations (P1-F5): only while some are pending, and only with sharing on. */}
        {!picker && <PendingShareInvitationsButton />}
        {!picker && refreshButton}
      </>
    )
  }

  return (
    <>
      <ul
        className='nav'
        style={{
          gap: 'var(--ob-spacing-2, 0.5rem)',
          margin: 0,
          padding: 0,
          flexWrap: 'wrap',
        }}
      >
        {!picker && (
          <li style={{margin: 0}}>
            <button
              type='button'
              data-tip
              data-for='create-folder'
              className={`btn btn-sm${newFolderOff ? ' ob-toolbar-disabled' : ''}`}
              style={labeledBtnStyle}
              aria-disabled={newFolderOff ? true : undefined}
              onMouseEnter={
                newFolderOff
                  ? undefined
                  : (e) => {
                      e.currentTarget.style.color = '#dbde8e'
                      e.currentTarget.style.borderColor = '#dbde8e'
                    }
              }
              onMouseLeave={(e) => {
                e.currentTarget.style.color = ''
                e.currentTarget.style.borderColor = ''
              }}
              onClick={onNewFolder}
            >
              <i className='fas fa-folder-plus' />
              <span className='ob-file-header__btn-label'>New Folder</span>
            </button>
            <ReactTooltip id='create-folder' effect='solid'>
              <span>{newFolderOff ?? 'New folder'}</span>
            </ReactTooltip>
          </li>
        )}
        {!picker && importDropdown}
        {!picker && (
          <ImportsDropdown appearance='labeled' onOpenFolder={fileTableContext.onOpenFolder} />
        )}
        {!picker && (
          <li style={{margin: 0}}>
            {refreshButton}
            <ReactTooltip id='refresh-files' effect='solid'>
              <span>Refresh file list</span>
            </ReactTooltip>
          </li>
        )}
      </ul>

      {toolbar.usage && (
        <div
          style={{
            color: 'var(--kt-text-muted)',
            fontSize: 'var(--ob-font-size-sm)',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--ob-spacing-1)',
          }}
        >
          <i className='fas fa-hdd' style={{fontSize: 'var(--ob-font-size-sm)'}}></i>
          <span>
            <span className='ob-file-header__usage-label'>Total usage: </span>
            {formatBytes(usage)}
          </span>
        </div>
      )}
    </>
  )
}

export const FileTableHeader = ({
  title,
  hideTitle,
  collapseProps,
  variant = 'default',
  onClose,
}: HeaderProps): JSX.Element => {
  const fileTableContext = useContext(FileTableContext)
  const isPanel = variant === 'panel'
  /** The wizard pick dialog: browse chrome only — no upload, import, new folder, or quota. */
  const isPicker = variant === 'picker'
  // Deep paths scroll rather than wrap in the narrow panel; keep the current folder in view.
  const breadcrumbRef = useRef<HTMLOListElement>(null)

  useEffect(() => {
    const el = breadcrumbRef.current
    if (el) {
      el.scrollLeft = el.scrollWidth
    }
  }, [fileTableContext.folderPath, fileTableContext.location])

  // The panel's breadcrumb strip matches the app's tab strips (same 34px strip, same text size).
  const crumbFontSize = 'var(--ob-font-size-sm)'
  // Absent from callers that only browse My Files (and from the header's own tests).
  const location = fileTableContext.location ?? {kind: 'own' as const, showHome: true}
  const crumbs = locationCrumbs(fileTableContext.folderPath, location)

  const goTo = (target: CrumbTarget) => {
    switch (target.to) {
      case 'home':
        fileTableContext.onGoHome?.()
        return
      case 'shared':
        fileTableContext.onGoShared?.()
        return
      case 'folder':
        fileTableContext.onBreadCrumb(target.index)
    }
  }

  const crumbLabel = (crumb: Crumb): React.ReactNode => {
    switch (crumb.kind) {
      case 'home':
        return (
          <i
            style={{
              cursor: 'pointer',
              textAlign: 'center',
              userSelect: 'none',
              fontSize: crumbFontSize,
              transition: 'color 0.2s ease',
            }}
            className='fas fa-home-alt'
            title='Home'
            aria-label='Home'
            data-testid='omics-files-breadcrumb-home'
          ></i>
        )
      default:
        return crumb.label
    }
  }

  const crumbTestId: Partial<Record<Crumb['kind'], string>> = {
    shared: 'omics-files-breadcrumb-shared',
    share: 'omics-files-breadcrumb-share',
  }

  // As before shared folders: the full-page browser shows no breadcrumb at the top of My Files.
  const showBreadcrumb =
    isPanel || isPicker || location.kind !== 'own' || fileTableContext.folderPath.length > 1

  const breadcrumbBar = showBreadcrumb ? (
    <ol
      ref={breadcrumbRef}
      className='breadcrumb ob-file-breadcrumb'
      aria-label='Current location'
      style={{
        margin: 0,
        padding: 0,
        fontSize: crumbFontSize,
        fontWeight: '500',
      }}
    >
      {crumbs.map((crumb, position) => (
        <li
          key={`${crumb.kind}-${position}`}
          className='breadcrumb-item'
          style={{margin: 0}}
          data-testid={crumbTestId[crumb.kind]}
          aria-current={position === crumbs.length - 1 ? 'location' : undefined}
          onClick={() => goTo(crumb.target)}
        >
          <div
            title={crumb.kind === 'home' ? undefined : crumb.label || undefined}
            className='ob-file-crumb'
          >
            {crumbLabel(crumb)}
          </div>
        </li>
      ))}
    </ol>
  ) : null

  // What can be done where the browser is (see toolbarActions). Absent: My Files.
  const toolbar = isPicker
    ? toolbarActions({picker: true, root: OWN_ROOT})
    : fileTableContext.toolbar ?? MY_FILES_TOOLBAR_ACTIONS

  return (
    <>
      {isPanel && (
        <div className='ob-files-panel__header'>
          <div className='ob-files-panel__header-left'>
            <div className='ob-files-panel__title-block'>
              <span className='ob-files-panel__title'>{title}</span>
            </div>
          </div>

          <div
            className='ob-files-panel__header-actions'
            data-testid='omics-files-panel-header-actions'
          >
            <FileTableChromeActions appearance='icon' toolbar={toolbar} picker={isPicker} />
            {onClose && (
              <button
                type='button'
                className='ob-files-panel__icon-btn d-lg-none'
                onClick={onClose}
                title='Close files'
                aria-label='Close files panel'
                data-testid='omics-files-panel-close'
              >
                <i className='fas fa-xmark' />
              </button>
            )}
          </div>
        </div>
      )}

      {isPanel ? (
        breadcrumbBar && (
          <div className='ob-files-panel__breadcrumbs' data-testid='omics-files-panel-breadcrumbs'>
            {breadcrumbBar}
          </div>
        )
      ) : (
        <div
          className='card-header border-0 pt-2'
          style={{
            minHeight: 'var(--ob-file-header-min-height, 70px)',
            paddingTop: 'var(--ob-spacing-2, 0.5rem)',
            paddingBottom: 'var(--ob-spacing-2, 0.5rem)',
          }}
        >
          {/* The pick dialogs have neither a title nor actions: only the breadcrumb is left. */}
          {!(isPicker && hideTitle) && (
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start'}}>
              {!hideTitle && (
                <>
                  <h2
                    style={{
                      fontSize: 'var(--ob-font-size-lg)',
                      fontWeight: '600',
                      margin: 0,
                      color: 'var(--kt-text-dark)',
                    }}
                  >
                    {title}
                  </h2>
                  <button {...collapseProps} className='collapse-icon collapse-icon-top'>
                    <i className='fa fa-window-minimize fs-5'></i>
                  </button>
                </>
              )}
            </div>
          )}

          {!isPicker && (
            <div className='card-toolbar'>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <FileTableChromeActions appearance='labeled' toolbar={toolbar} picker={isPicker} />
              </div>
            </div>
          )}

          {breadcrumbBar && (
            <div
              className='card-toolbar'
              style={{
                marginTop: 'var(--ob-file-breadcrumb-margin-top, var(--ob-spacing-2))',
                paddingLeft: 'var(--ob-spacing-2)',
              }}
            >
              {breadcrumbBar}
            </div>
          )}
        </div>
      )}
    </>
  )
}
